import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Platform,
  ActivityIndicator,
  ScrollView,
  TextInput,
  Modal,
  FlatList,
  RefreshControl,
  Animated,
  LayoutAnimation,
  UIManager,
  Pressable,
} from 'react-native';
import { overlayBackdropStyle, overlayCardLook } from '../components/OverlayPanel';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { ScreenLayout, Header, FormHeader, ConfirmationModal, Dropdown, DropdownOption } from '../components';
import { countActive, FilterField, fv, QuickView, relativeRange, SortOption, useListFilters } from '../components/listFilters/listFilters';
import { FilterSheet } from '../components/listFilters/FilterSheet';
import { ListFilterBar } from '../components/listFilters/ListFilterBar';
import { DocumentModal } from '../components/documents/DocumentModal';
import { ORDER_DOCUMENT_TYPES } from '../utils/documentTypes';
import { usePermissions } from '../hooks/usePermissions';
import { searchProducts, searchVendors } from '../components/listFilters/entitySearch';
import { useGoBack } from '../hooks/useGoBack';
import { apiService, syncApiServiceWithStore } from '../services/api';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography, withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';
import { getCurrencySymbol } from '../utils/currencies';
import { useThemedRTLStyles } from '../hooks/useThemedRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { businessCurrency } from '../utils/businessDefaults';

interface Order {
  _id: string;
  orderNumber: string;
  customer: {
    name: string;
    phone: string;
    email?: string;
    address?: string;
    location?: {
      latitude?: number;
      longitude?: number;
      address?: string;
    };
  };
  items: Array<{
    productId?: string;
    variantId?: string;
    product: {
      name: string;
      price: number;
      salePrice?: number;
      brand?: string;
      sku?: string;
      primaryImage?: string;
      productType?: 'physical' | 'digital' | 'service';
      priceUnit?: string;
    };
    variant?: { id?: string; name?: string; price?: number };
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    selectedAddons?: Array<{
      addonId: string;
      name: string;
      price: number;
      quantity: number;
    }>;
  }>;
  subtotal?: number;
  tax?: number;
  shipping?: number;
  discount?: number;
  totalPrice: number;
  currency: string;
  orderStatus: 'pending' | 'placed' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  paymentStatus?: 'unpaid' | 'partial' | 'paid' | 'refunded';
  source?: 'checkout' | 'catalog' | 'quick_reply' | 'manual' | 'api';
  orderDate: string;
  confirmedAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
  invoiceNumber?: string;
  invoiceUrl?: string;
  shippingAddress?: string;
  shippingMethod?: string;
  shippingMethodId?: string;
  shippingMethodSnapshot?: { name: string; type: string; price: number; providerName?: string };
  trackingNumber?: string;
  notes?: string;
}

// Order fields filterable by their stored values ("More fields" in the filter
// sheet, next to custom fields) — matches ORDER_FIXED_FIELD_PATHS server-side.
const ORDER_VALUE_FIELDS = ['currency'];

interface OrdersScreenProps {
  /** Set when this screen is reached via a system Orders Data List's scoped
   * mode (see DataListRecordsScreen.tsx) — restricts the list to only
   * records added THROUGH that list, and shows an exit banner back to it. */
  scopedToDataListId?: string;
  scopedListName?: string;
}

export const OrdersScreen: React.FC<OrdersScreenProps> = ({ scopedToDataListId, scopedListName }) => {
  const styles = useThemedRTLStyles(getStyles);
  const { t } = useLocale();
  const { currentBusiness } = useAppStore();
  // Columns follow the list's own width (on desktop it shares the screen
  // with side panes, so the window width says nothing): one column on
  // phones, more where cards would otherwise stretch
  const [listWidth, setListWidth] = useState(0);
  const columns = listWidth >= 1100 ? 3 : listWidth >= 680 ? 2 : 1;
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const goBack = useGoBack('home');

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 20; // Items per page
  
  // Filters — the shared list filters: remembered per screen, quick views + saved views.
  const quickViews = useMemo<QuickView[]>(() => [
    { key: 'toFulfil', label: t('orders.view.toFulfil'), icon: 'cube-outline', filters: { orderStatus: ['placed', 'confirmed', 'processing'] } },
    { key: 'unpaid', label: t('orders.view.unpaid'), icon: 'card-outline', filters: { paymentStatus: ['unpaid', 'partial'] } },
    { key: 'today', label: t('orders.view.today'), icon: 'today-outline', filters: { orderDate: relativeRange.today() } },
    { key: 'week', label: t('orders.view.thisWeek'), icon: 'calendar-outline', filters: { orderDate: relativeRange.thisWeek() } },
    { key: 'cancelled', label: t('orders.status.cancelled'), icon: 'close-circle-outline', filters: { orderStatus: ['cancelled'] } },
  ], [t]);
  const list = useListFilters(scopedToDataListId ? `orders:${scopedToDataListId}` : 'orders', { quickViews });
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter dropdown states (inline dropdowns instead of modals)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  
  // Status modal state for order cards
  const [statusModalOrderId, setStatusModalOrderId] = useState<string | null>(null);
  const [paymentStatusModalOrderId, setPaymentStatusModalOrderId] = useState<string | null>(null);
  
  // Refs for dropdown containers to detect outside clicks
  const orderStatusDropdownRef = useRef<View>(null);
  const paymentStatusDropdownRef = useRef<View>(null);
  const sourceDropdownRef = useRef<View>(null);
  const dateRangeDropdownRef = useRef<View>(null);
  
  // Date range animation (kept for date picker)
  const dateRangeAnim = useRef(new Animated.Value(0)).current;
  
  // Set active tab to 'orders' when this screen loads
  useEffect(() => {
    console.log('🔵 [OrdersScreen] Setting activeTab to orders, current activeTab:', activeTab);
    setActiveTab('orders');
  }, [setActiveTab]);
  
  // Enable LayoutAnimation on Android
  useEffect(() => {
    if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
      UIManager.setLayoutAnimationEnabledExperimental(true);
    }
  }, []);
  
  useEffect(() => {
    Animated.timing(dateRangeAnim, {
      toValue: showDateRangePicker ? 1 : 0,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [showDateRangePicker, dateRangeAnim]);
  
  // Handle dropdown toggle
  const handleDropdownToggle = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  // Track if user is scrolling to prevent closing
  const isScrollingRef = useRef(false);
  
  // Web: Add global click listener to detect clicks outside dropdowns
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      let clickTimeout: NodeJS.Timeout | null = null;
      
      const handleDocumentClick = (e: MouseEvent) => {
        // Clear any pending timeout
        if (clickTimeout) {
          clearTimeout(clickTimeout);
          clickTimeout = null;
        }
        
        // Don't close if user is scrolling
        if (isScrollingRef.current) {
          return;
        }
        
        const target = e.target as HTMLElement;
        if (!target) return;
        
        // Check if click is inside any dropdown - check all parent elements
        let isInsideDropdown = false;
        let element: HTMLElement | null = target;
        
        while (element) {
          if (
            element.hasAttribute('data-dropdown-wrapper') ||
            element.hasAttribute('data-dropdown-list') ||
            element.hasAttribute('data-status-dropdown') ||
            element.closest('[data-dropdown-wrapper]') !== null ||
            element.closest('[data-dropdown-list]') !== null ||
            element.closest('[data-status-dropdown]') !== null ||
            element.closest('[data-payment-status-dropdown]') !== null
          ) {
            isInsideDropdown = true;
            break;
          }
          element = element.parentElement;
        }
        
        if (!isInsideDropdown) {
          // Use setTimeout to allow dropdown item onClick to fire first
          clickTimeout = setTimeout(() => {
            // Double-check that dropdowns are still open (they might have been closed by item selection)
            if (openDropdown || showDateRangePicker || statusModalOrderId || paymentStatusModalOrderId) {
              if (openDropdown) {
                setOpenDropdown(null);
              }
              if (showDateRangePicker) {
                setShowDateRangePicker(false);
              }
              if (statusModalOrderId) {
                setStatusModalOrderId(null);
              }
              if (paymentStatusModalOrderId) {
                setPaymentStatusModalOrderId(null);
              }
            }
          }, 50); // Increased delay to ensure item selection fires first
        }
      };
      
      // Only add listener when dropdowns are open
      if (openDropdown || showDateRangePicker || statusModalOrderId || paymentStatusModalOrderId) {
        // Use capture phase but with delay to allow item onClick to fire first
        document.addEventListener('click', handleDocumentClick, false);
        return () => {
          if (clickTimeout) {
            clearTimeout(clickTimeout);
          }
          document.removeEventListener('click', handleDocumentClick, false);
        };
      }
    }
  }, [openDropdown, showDateRangePicker, statusModalOrderId, paymentStatusModalOrderId]);
  
  // Close all dropdowns when clicking outside
  const handleCloseDropdowns = (e?: any) => {
    // Don't close if user is currently scrolling
    if (isScrollingRef.current) {
      return;
    }
    
    // On mobile, check if the touch is inside the status dropdowns
    if (Platform.OS !== 'web' && e?.nativeEvent) {
      // On mobile, we need to be more careful about closing dropdowns
      // The status dropdowns should handle their own closing
      if (statusModalOrderId || paymentStatusModalOrderId) {
        // Don't close status modals from overlay - let them handle themselves
        return;
      }
    }
    
    // On web, check if click is inside a dropdown using the actual DOM event
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      // Use the most recent click event from the document
      const checkInsideDropdown = () => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
          const isInsideDropdown = 
            activeElement.closest?.('[data-dropdown-wrapper]') !== null ||
            activeElement.closest?.('[data-dropdown-list]') !== null ||
            activeElement.closest?.('[data-status-dropdown]') !== null;
          
          if (isInsideDropdown) {
            return true;
          }
        }
        
        // Also check event target if available
        if (e?.nativeEvent?.target) {
          const target = e.nativeEvent.target as HTMLElement;
          const isInsideDropdown = 
            target.closest?.('[data-dropdown-wrapper]') !== null ||
            target.closest?.('[data-dropdown-list]') !== null ||
            target.closest?.('[data-status-dropdown]') !== null;
          
          if (isInsideDropdown) {
            return true;
          }
        }
        
        return false;
      };
      
      if (checkInsideDropdown()) {
        return;
      }
    }
    
    if (openDropdown) {
      setOpenDropdown(null);
    }
    if (showDateRangePicker) {
      setShowDateRangePicker(false);
    }
    if (statusModalOrderId) {
      setStatusModalOrderId(null);
    }
    if (paymentStatusModalOrderId) {
      setPaymentStatusModalOrderId(null);
    }
  };

  // Dropdown options
  const orderStatusOptions: DropdownOption[] = [
    { value: 'pending', label: t('orders.status.pending') },
    { value: 'placed', label: t('orders.status.placed') },
    { value: 'confirmed', label: t('orders.status.confirmed') },
    { value: 'processing', label: t('orders.status.processing') },
    { value: 'shipped', label: t('orders.status.shipped') },
    { value: 'delivered', label: t('orders.status.delivered') },
    { value: 'cancelled', label: t('orders.status.cancelled') },
  ];

  const paymentStatusOptions: DropdownOption[] = [
    { value: 'unpaid', label: t('orders.payment.unpaid') },
    { value: 'partial', label: t('orders.payment.partial') },
    { value: 'paid', label: t('orders.payment.paid') },
    { value: 'refunded', label: t('orders.payment.refunded') },
  ];

  const sourceOptions: DropdownOption[] = [
    { value: 'checkout', label: t('orders.source.checkout') },
    { value: 'catalog', label: t('orders.source.catalog') },
    { value: 'quick_reply', label: t('orders.source.quick_reply') },
    { value: 'manual', label: t('orders.source.manual') },
    { value: 'api', label: t('orders.source.api') },
  ];

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const orderFields = useMemo<FilterField[]>(() => [
    { key: 'orderStatus', label: t('orders.filter.orderStatus'), type: 'multi', section: t('orders.filterSection.order'), options: orderStatusOptions.map((o) => ({ value: String(o.value), label: o.label })) },
    { key: 'paymentStatus', label: t('orders.filter.paymentStatus'), type: 'multi', section: t('orders.filterSection.order'), options: paymentStatusOptions.map((o) => ({ value: String(o.value), label: o.label })) },
    { key: 'source', label: t('orders.filter.source'), type: 'multi', section: t('orders.filterSection.order'), options: sourceOptions.map((o) => ({ value: String(o.value), label: o.label })) },
    { key: 'orderDate', label: t('orders.filter.orderDate'), type: 'date', section: t('orders.filterSection.order') },
    { key: 'total', label: t('orders.filter.total'), type: 'range', unit: businessCurrency(), section: t('orders.filterSection.order') },
    { key: 'vendors', label: t('orders.filter.vendors'), type: 'entity', search: searchVendors, section: t('orders.filterSection.items'), hint: t('orders.filter.vendorsHint') },
    { key: 'products', label: t('orders.filter.products'), type: 'entity', search: searchProducts, section: t('orders.filterSection.items') },
    { key: 'hasInvoice', label: t('orders.filter.invoice'), type: 'choice', section: t('orders.filterSection.documents'), options: [{ value: 'yes', label: t('orders.filter.invoiceYes') }, { value: 'no', label: t('orders.filter.invoiceNo') }] },
    { key: 'slip', label: t('orders.filter.slip'), type: 'choice', section: t('orders.filterSection.documents'), options: [{ value: 'yes', label: t('orders.filter.slipYes') }, { value: 'no', label: t('orders.filter.slipNo') }] },
  ], [t]);
  const orderSorts = useMemo<SortOption[]>(() => [
    { key: 'newest', label: t('orders.sort.newest'), sortBy: 'orderDate', sortOrder: 'desc', icon: 'arrow-down-outline' },
    { key: 'oldest', label: t('orders.sort.oldest'), sortBy: 'orderDate', sortOrder: 'asc', icon: 'arrow-up-outline' },
    { key: 'totalDesc', label: t('orders.sort.totalDesc'), sortBy: 'totalPrice', sortOrder: 'desc', icon: 'trending-up-outline' },
    { key: 'totalAsc', label: t('orders.sort.totalAsc'), sortBy: 'totalPrice', sortOrder: 'asc', icon: 'trending-down-outline' },
    { key: 'updated', label: t('orders.sort.updated'), sortBy: 'updatedAt', sortOrder: 'desc', icon: 'time-outline' },
  ], [t]);

  // Custom fields (and value-backed order fields) for "More fields" in the filter sheet.
  const [customFieldKeys, setCustomFieldKeys] = useState<string[]>([]);
  useEffect(() => {
    apiService.getOrderCustomFieldKeys().then((res) => {
      if (res.statusCode === 1000 && res.data) setCustomFieldKeys(res.data);
    }).catch(() => {});
  }, []);
  const customFieldsConfig = useMemo(() => ({
    fields: [...ORDER_VALUE_FIELDS.map((key) => ({ key, label: t(`orders.field.${key}` as any) })), ...customFieldKeys.map((key) => ({ key, label: key }))],
    loadValues: async (key: string) => (await apiService.getOrderCustomFieldValues(key)).data || [],
  }), [customFieldKeys, t]);

  useEffect(() => {
    setActiveTab('orders');
  }, [setActiveTab]);

  // Reload whenever the filters change (search debounced), once remembered filters are read.
  const filterKey = JSON.stringify([list.state.filters, list.state.sort, list.state.customFields, list.state.search]);
  useEffect(() => {
    if (!list.ready) return;
    const timer = setTimeout(() => loadOrders(true), list.state.search ? 450 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.ready, filterKey]);

  const loadOrders = useCallback(async (reset = false, loadMore = false) => {
    try {
      if (reset) {
      setError(null);
      setLoading(true);
        setCurrentPage(0);
        setHasMore(true);
        setOrders([]);
      } else if (loadMore) {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
      } else {
        setError(null);
        setLoading(true);
      }

      const page = reset ? 0 : (loadMore ? currentPage + 1 : currentPage);
      const offset = page * limit;

      const filterParams: any = {
        limit,
        offset,
      };
      
      const f = list.state.filters;
      const sort = orderSorts.find((s) => s.key === list.state.sort) || orderSorts[0];
      Object.assign(filterParams, {
        orderStatus: fv.csv(f.orderStatus),
        paymentStatus: fv.csv(f.paymentStatus),
        source: fv.csv(f.source),
        search: fv.text(list.state.search),
        orderDateFrom: f.orderDate?.from ? `${f.orderDate.from}T00:00:00.000` : undefined,
        orderDateTo: f.orderDate?.to ? `${f.orderDate.to}T23:59:59.999` : undefined,
        totalMin: fv.min(f.total),
        totalMax: fv.max(f.total),
        vendorIds: fv.csv(f.vendors),
        productIds: fv.csv(f.products),
        hasInvoice: f.hasInvoice,
        slip: f.slip,
        dataListId: scopedToDataListId,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        customFieldFilters: fv.customFields(list.state.customFields),
      });

      const response = await apiService.getOrders(filterParams);

      if (response.statusCode === 1000 && response.data) {
        const ordersData = response.data as Order[];
        const paginationInfo = (response as any).additionalInfo;
        const responseTotal = paginationInfo?.total || ordersData.length;
        
        if (loadMore) {
          setOrders(prev => [...prev, ...ordersData]);
        } else {
        setOrders(ordersData);
        }
        
        // Check if there are more items to load
        setHasMore(paginationInfo?.hasMore ?? (ordersData.length === limit));
        setCurrentPage(page);
        setTotal(responseTotal);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to load orders';
        setError(errorMessage);
        if (!loadMore) {
        showNotification('error', t('orders.common.error'), errorMessage);
        }
      }
    } catch (error: any) {
      console.error('Error loading orders:', error);
      const errorMessage = error.message || 'Failed to load orders';
      setError(errorMessage);
      if (!loadMore) {
      showNotification('error', t('orders.common.error'), errorMessage);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [list.state, orderSorts, currentPage, hasMore, loadingMore, limit, showNotification, scopedToDataListId]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    loadOrders(true);
  }, [loadOrders]);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      loadOrders(false, true);
    }
  }, [loadOrders, loadingMore, hasMore, loading]);

  const handleOrderPress = (order: Order) => {
    navigateTo('order-edit', order._id, scopedToDataListId ? { listId: scopedToDataListId } : undefined);
  };

  const handleCreateOrder = () => {
    // Navigate to add order screen in new mode
    navigateTo('order-add', undefined, scopedToDataListId ? { listId: scopedToDataListId } : undefined);
  };

  const handleAddProduct = () => {
    // Navigate to products screen where user can select catalogue and add product
    // The products screen will handle navigation to add product with catalogueId
    navigateTo('products');
  };

  const handleCreateQuotation = () => {
    navigateTo('quotation-create');
  };

  const handleUpdateStatusFromCard = async (orderId: string, status: string) => {
    setStatusModalOrderId(null);
    
    try {
      const tokens = useAppStore.getState().tokens;
      if (tokens?.token) {
        syncApiServiceWithStore(tokens);
      } else {
        showNotification('error', t('orders.common.error'), t('orders.msg.loginAgain'));
        return;
      }
      
      const response = await apiService.updateOrder(orderId, {
        orderStatus: status as any,
      });

      if (response.statusCode === 1000) {
        showNotification('success', t('orders.common.success'), t('orders.msg.statusUpdated'));
        loadOrders();
      } else {
        const errorMessage = response.errors?.[0]?.message || t('orders.msg.statusFailed');
        showNotification('error', t('orders.common.error'), errorMessage);
      }
    } catch (error: any) {
      showNotification('error', t('orders.common.error'), error.message || t('orders.msg.statusFailed'));
    }
  };

  const handleUpdatePaymentStatusFromCard = async (orderId: string, paymentStatus: string) => {
    setPaymentStatusModalOrderId(null);
    
    try {
      const tokens = useAppStore.getState().tokens;
      if (tokens?.token) {
        syncApiServiceWithStore(tokens);
      } else {
        showNotification('error', t('orders.common.error'), t('orders.msg.loginAgain'));
        return;
      }
      
      const response = await apiService.updateOrder(orderId, {
        paymentStatus: paymentStatus as any,
      });

      if (response.statusCode === 1000) {
        showNotification('success', t('orders.common.success'), t('orders.msg.paymentUpdated'));
        loadOrders();
      } else {
        const errorMessage = response.errors?.[0]?.message || t('orders.msg.paymentFailed');
        showNotification('error', t('orders.common.error'), errorMessage);
      }
    } catch (error: any) {
      showNotification('error', t('orders.common.error'), error.message || t('orders.msg.paymentFailed'));
    }
  };

  // Template design is for people who can edit orders (same rule as the templates API).
  const { hasPermissionAtLeast } = usePermissions();
  const canEditTemplates = hasPermissionAtLeast('general.manageOrders', 'edit');

  // Documents (proforma, confirmation, invoice, receipt, delivery note) for one order.
  const [documentsOrder, setDocumentsOrder] = useState<Order | null>(null);

  const handleOpenInvoice = (url: string) => {
    if (Platform.OS === 'web') {
      window.open(url, '_blank');
    }
  };

  const handleGenerateInvoice = async (order: Order, regenerate = false) => {
    try {
      showNotification('info', regenerate ? t('orders.msg.regenerating') : t('orders.msg.generating'), t('orders.msg.generatingInvoice'));
      const response = await apiService.generateOrderInvoice(order._id, regenerate);
      if (response.statusCode === 1000 && response.data) {
        showNotification('success', t('orders.common.success'), regenerate ? t('orders.msg.invoiceRegenerated') : t('orders.msg.invoiceGenerated'));
        loadOrders();
        handleOpenInvoice(response.data.invoiceUrl);
      } else {
        const errorMessage = response.errors?.[0]?.message || t('orders.msg.invoiceFailed');
        showNotification('error', t('orders.common.error'), errorMessage);
      }
    } catch (error: any) {
      console.error('Error generating invoice:', error);
      showNotification('error', t('orders.common.error'), error.message || t('orders.msg.invoiceFailed'));
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteOrder(orderToDelete);

      if (response.statusCode === 1000) {
        showNotification('success', t('orders.common.success'), t('orders.msg.deleted'));
        setShowDeleteModal(false);
        setOrderToDelete(null);
        loadOrders();
      } else {
        const errorMessage = response.errors?.[0]?.message || t('orders.msg.deleteFailed');
        showNotification('error', t('orders.common.error'), errorMessage);
        setShowDeleteModal(false);
        setOrderToDelete(null);
      }
    } catch (error: any) {
      console.error('Error deleting order:', error);
      showNotification('error', t('orders.common.error'), error.message || t('orders.msg.deleteFailed'));
      setShowDeleteModal(false);
      setOrderToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'placed':
      case 'confirmed':
        return colors.success;
      case 'processing':
      case 'shipped':
        return colors.warning;
      case 'delivered':
        return colors.success;
      case 'cancelled':
        return colors.error;
      default:
        return colors.textSecondary;
    }
  };

  const getPaymentStatusColor = (status?: string) => {
    switch (status) {
      case 'paid':
        return colors.success;
      case 'partial':
        return colors.warning;
      case 'unpaid':
        return colors.error;
      default:
        return colors.textSecondary;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string = businessCurrency()) => {
    return `${getCurrencySymbol(currency)} ${amount.toFixed(2)}`;
  };

  const activeFiltersCount = countActive(orderFields, list.state);

  const renderOrderItem = ({ item }: { item: Order }) => {
    const isDropdownOpen = false; // No longer using dropdowns, using modals instead
    return (
    <View
      style={[
        styles.orderCard,
        columns > 1 && styles.orderCardInGrid,
        isDropdownOpen && styles.orderCardWithOpenDropdown,
      ]}
    >
    <TouchableOpacity
      onPress={() => {
        if (!isDropdownOpen) {
          handleOrderPress(item);
        }
      }}
      activeOpacity={0.7}
      style={{ flex: 1 }}
      disabled={isDropdownOpen}
    >
      <View style={styles.orderHeader}>
        <View style={styles.orderHeaderLeft}>
          <View style={styles.orderNumberRow}>
            <Text style={styles.orderNumber}>{item.orderNumber}</Text>
            <TouchableOpacity
              style={styles.copyButton}
              onPress={async () => {
                try {
                  if (Platform.OS === 'web' && navigator.clipboard) {
                    await navigator.clipboard.writeText(item.orderNumber);
                  } else {
                    await Clipboard.setStringAsync(item.orderNumber);
                  }
                  showNotification('success', t('orders.msg.copied'), t('orders.msg.numberCopied'));
                } catch (error) {
                  console.error('Failed to copy order number:', error);
                  showNotification('error', t('orders.common.error'), t('orders.msg.copyFailed'));
                }
              }}
              activeOpacity={0.7}
            >
              <Ionicons name="copy-outline" size={16} color={colors.primary} />
            </TouchableOpacity>
          </View>
          <Text style={styles.customerName}>{item.customer.name}</Text>
        </View>
        <View style={styles.orderHeaderRight} pointerEvents="box-none">
          <TouchableOpacity
            style={[styles.statusBadge, { backgroundColor: withAlpha(getStatusColor(item.orderStatus), 0.13) }]}
            onPress={() => setStatusModalOrderId(item._id)}
            activeOpacity={0.7}
          >
            <Text style={[styles.statusText, { color: getStatusColor(item.orderStatus) }]}>
              {t(`orders.status.${item.orderStatus}` as any) === `orders.status.${item.orderStatus}` ? item.orderStatus : t(`orders.status.${item.orderStatus}` as any)}
            </Text>
            <Ionicons name="chevron-down" size={12} color={getStatusColor(item.orderStatus)} />
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.orderDetails}>
        <View style={styles.orderDetailRow}>
          <Ionicons name="calendar-outline" size={16} color={colors.textSecondary} />
          <Text style={styles.orderDetailText}>{formatDate(item.orderDate)}</Text>
        </View>
        <View style={styles.orderDetailRow}>
          <Ionicons name="cube-outline" size={16} color={colors.textSecondary} />
          <Text style={styles.orderDetailText}>
            {t('orders.list.itemsCount', { count: item.items.length })}
          </Text>
        </View>
        {item.paymentStatus && (
          <View style={styles.orderDetailRow}>
            <TouchableOpacity
              style={[styles.statusBadge, { backgroundColor: withAlpha(getPaymentStatusColor(item.paymentStatus), 0.13) }]}
              onPress={() => setPaymentStatusModalOrderId(item._id)}
              activeOpacity={0.7}
            >
              <Ionicons name="card-outline" size={16} color={getPaymentStatusColor(item.paymentStatus)} />
              <Text style={[styles.statusText, { color: getPaymentStatusColor(item.paymentStatus) }]}>
                {t(`orders.payment.${item.paymentStatus}` as any) === `orders.payment.${item.paymentStatus}` ? item.paymentStatus : t(`orders.payment.${item.paymentStatus}` as any)}
              </Text>
              <Ionicons name="chevron-down" size={12} color={getPaymentStatusColor(item.paymentStatus)} />
            </TouchableOpacity>
          </View>
        )}
      </View>

      <View style={styles.orderFooter}>
        <Text style={styles.orderTotal}>{formatCurrency(item.totalPrice, item.currency)}</Text>
        <View style={styles.orderActions}>
          {/* Edit Order Button */}
          <TouchableOpacity
            style={styles.actionButton}
            onPress={(e) => {
              e.stopPropagation();
              handleOrderPress(item);
            }}
          >
            <Ionicons name="create-outline" size={18} color={colors.primary} />
            <Text style={styles.actionButtonText}>{t('orders.action.edit')}</Text>
          </TouchableOpacity>
          {item.invoiceUrl ? (
            <>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => { e.stopPropagation(); handleOpenInvoice(item.invoiceUrl!); }}
              >
                <Ionicons name="document-text-outline" size={18} color={colors.primary} />
                <Text style={styles.actionButtonText}>{t('orders.action.invoice')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => { e.stopPropagation(); handleGenerateInvoice(item, true); }}
              >
                <Ionicons name="refresh-outline" size={18} color={colors.primary} />
                <Text style={styles.actionButtonText}>{t('orders.action.regen')}</Text>
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity
              style={styles.actionButton}
              onPress={(e) => { e.stopPropagation(); handleGenerateInvoice(item); }}
            >
              <Ionicons name="receipt-outline" size={18} color={colors.primary} />
              <Text style={styles.actionButtonText}>{t('orders.action.invoice')}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={styles.actionButton}
            onPress={(e) => { e.stopPropagation(); setDocumentsOrder(item); }}
            accessibilityLabel={t('documents.title')}
          >
            <Ionicons name="documents-outline" size={18} color={colors.primary} />
            <Text style={styles.actionButtonText}>{t('documents.title')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </TouchableOpacity>
    </View>
  );
  };

  const renderContent = () => {
    if (loading && orders.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>{t('orders.list.loading')}</Text>
        </View>
      );
    }

    if (error && orders.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorTitle}>{t('orders.list.loadErrorTitle')}</Text>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => loadOrders()}
          >
            <Text style={styles.retryButtonText}>{t('orders.list.tryAgain')}</Text>
          </TouchableOpacity>
        </View>
      );
    }

    if (orders.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="receipt-outline" size={64} color={colors.textSecondary} />
          <Text style={styles.emptyTitle}>{t('orders.list.emptyTitle')}</Text>
          <Text style={styles.emptyText}>{t('orders.list.emptyText')}</Text>
          <TouchableOpacity
            style={styles.createButton}
            onPress={handleCreateOrder}
          >
            <Ionicons name="add-circle-outline" size={20} color={colors.white} />
            <Text style={styles.createButtonText}>{t('orders.list.createOrder')}</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <FlatList
        // numColumns can't change on the fly: remount when it does
        key={`orders-${columns}`}
        numColumns={columns}
        columnWrapperStyle={columns > 1 ? styles.gridRow : undefined}
        data={orders}
        renderItem={renderOrderItem}
        keyExtractor={(item) => item._id}
        contentContainerStyle={styles.listContent}
        onLayout={(e) => setListWidth(e.nativeEvent.layout.width)}
        style={Platform.OS !== 'web' ? { zIndex: 1 } : {}} // Lower z-index than dropdowns (only on mobile)
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
          />
        }
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={
          loadingMore ? (
            <View style={styles.loadingMoreContainer}>
              <ActivityIndicator size="small" color={colors.primary} />
              <Text style={styles.loadingMoreText}>{t('orders.list.loadingMore')}</Text>
            </View>
          ) : null
        }
      />
    );
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onTabPress={handleTabPress}
      formHeader={
        <FormHeader
          title={scopedToDataListId ? (scopedListName || 'Orders') : 'Orders'}
          subtitle={
            !loading
              ? `${total} ${total === 1 ? 'order' : 'orders'}${activeFiltersCount > 0 ? ` • ${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''}` : ''}`
              : undefined
          }
          onBack={goBack}
          actions={[
            { icon: 'document-text-outline', onPress: handleCreateQuotation, label: t('orders.toolbar.createQuotation'), accessibilityLabel: t('orders.toolbar.createQuotation') },
            ...(canEditTemplates ? [{ icon: 'documents-outline' as const, onPress: () => navigateTo('quotation-templates'), label: t('documents.templatesTitle'), accessibilityLabel: t('documents.templatesTitle') }] : []),
            { icon: 'add', primary: true, onPress: handleCreateOrder, label: t('orders.list.createOrder'), accessibilityLabel: t('orders.list.createOrder') },
          ]}
        />
      }
    >
      <View style={styles.container}>
        {scopedToDataListId && (
          /* Scoped mode — this list is showing only records added through a
             System Data List. Two distinct exits: leaving to the Data Lists
             index vs. staying on Orders but dropping the scope filter —
             collapsing them into one button pointed at data-list-records
             caused an exit → immediate bounce-back-in loop, since
             DataListRecordsScreen unconditionally redirects any system list
             straight back into this same scoped screen. Body content, not
             header chrome (conditional/temporary, unlike FormHeader's fixed
             row) — matches ProductsListScreen's identical pattern. */
          <View style={styles.scopedBannerWrapper}>
            <View style={styles.scopedExitRow}>
              <TouchableOpacity
                style={styles.scopedExitBanner}
                onPress={() => navigateTo('data-lists')}
                activeOpacity={0.7}
              >
                <Ionicons name="exit-outline" size={15} color={colors.primary} />
                <Text style={styles.scopedExitBannerText}>{t('orders.list.exitToDataLists')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.scopedExitBanner}
                onPress={() => navigateTo('orders', undefined, {})}
                activeOpacity={0.7}
              >
                <Ionicons name="receipt-outline" size={15} color={colors.primary} />
                <Text style={styles.scopedExitBannerText}>{t('orders.list.exitToOrders')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Search · Filters · Sort, quick / saved views and active filters */}
        <ListFilterBar
          controller={list}
          fields={orderFields}
          sorts={orderSorts}
          onOpenFilters={() => setShowFilters(true)}
          searchPlaceholder={t('orders.filter.search')}
          customFieldLabels={Object.fromEntries(customFieldsConfig.fields.map((f) => [f.key, f.label]))}
        />

        {/* Content */}
        {renderContent()}
      </View>

      {/* Status Update Modal */}
      <Modal
        visible={!!statusModalOrderId}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setStatusModalOrderId(null)}
      >
        <TouchableWithoutFeedback onPress={() => setStatusModalOrderId(null)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.statusModalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>{t('orders.modal.updateStatus')}</Text>
                  <TouchableOpacity
                    onPress={() => setStatusModalOrderId(null)}
                    style={styles.modalCloseButton}
                  >
                    <Ionicons name="close" size={24} color={colors.text.primary} />
                  </TouchableOpacity>
                </View>

                <ScrollView style={styles.modalBody}>
                  {['pending', 'placed', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => {
                    const currentOrder = orders.find(o => o._id === statusModalOrderId);
                    const isSelected = currentOrder?.orderStatus === status;
                    return (
                      <TouchableOpacity
                        key={status}
                        style={[
                          styles.statusOption,
                          isSelected && styles.statusOptionActive,
                        ]}
                        onPress={() => {
                          if (statusModalOrderId) {
                            handleUpdateStatusFromCard(statusModalOrderId, status);
                            setStatusModalOrderId(null);
                          }
                        }}
                      >
                        <Text
                          style={[
                            styles.statusOptionText,
                            { color: getStatusColor(status) },
                            isSelected && styles.statusOptionTextActive,
                          ]}
                        >
                          {status.toUpperCase()}
                        </Text>
                        {isSelected && (
                          <Ionicons name="checkmark" size={20} color={colors.primary} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Payment Status Update Modal */}
      <Modal
        visible={!!paymentStatusModalOrderId}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setPaymentStatusModalOrderId(null)}
      >
        <TouchableWithoutFeedback onPress={() => setPaymentStatusModalOrderId(null)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.statusModalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>{t('orders.modal.updatePayment')}</Text>
                  <TouchableOpacity
                    onPress={() => setPaymentStatusModalOrderId(null)}
                    style={styles.modalCloseButton}
                  >
                    <Ionicons name="close" size={24} color={colors.text.primary} />
                  </TouchableOpacity>
                </View>

                <ScrollView style={styles.modalBody}>
                  {['unpaid', 'partial', 'paid', 'refunded'].map((status) => {
                    const currentOrder = orders.find(o => o._id === paymentStatusModalOrderId);
                    const isSelected = currentOrder?.paymentStatus === status;
                    return (
                      <TouchableOpacity
                        key={status}
                        style={[
                          styles.statusOption,
                          isSelected && styles.statusOptionActive,
                        ]}
                        onPress={() => {
                          if (paymentStatusModalOrderId) {
                            handleUpdatePaymentStatusFromCard(paymentStatusModalOrderId, status);
                            setPaymentStatusModalOrderId(null);
                          }
                        }}
                      >
                        <Ionicons name="card-outline" size={18} color={getPaymentStatusColor(status)} />
                        <Text
                          style={[
                            styles.statusOptionText,
                            { color: getPaymentStatusColor(status) },
                            isSelected && styles.statusOptionTextActive,
                          ]}
                        >
                          {status.toUpperCase()}
                        </Text>
                        {isSelected && (
                          <Ionicons name="checkmark" size={20} color={colors.primary} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>


      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteModal}
        title={t('orders.delete.title')}
        message={t('orders.delete.message')}
        confirmText={t('orders.delete.confirm')}
        cancelText={t('orders.common.cancel')}
        onConfirm={handleDeleteOrder}
        onCancel={() => {
          setShowDeleteModal(false);
          setOrderToDelete(null);
        }}
        loading={deleting}
        confirmButtonStyle={{ backgroundColor: colors.error }}
      />

      {documentsOrder && (
        <DocumentModal
          sendSource={{ orderId: documentsOrder._id }}
          visible={!!documentsOrder}
          types={ORDER_DOCUMENT_TYPES}
          scopeLabel={t('documents.forOrder', { number: documentsOrder.orderNumber })}
          recipient={{ name: documentsOrder.customer?.name, phone: documentsOrder.customer?.phone, email: documentsOrder.customer?.email }}
          unavailable={['paid', 'partial'].includes(String(documentsOrder.paymentStatus)) ? {} : { receipt: t('documents.receiptNeedsPayment') }}
          onClose={() => setDocumentsOrder(null)}
          onGenerate={async (req) => {
            const res = await apiService.generateOrderDocument(documentsOrder._id, req);
            if (res.statusCode !== 1000 || !res.data) throw new Error((res as any).message || res.errors?.[0]?.message || t('documents.failed'));
            if (req.type === 'invoice') loadOrders(true);
            return res.data;
          }}
        />
      )}

      <FilterSheet
        visible={showFilters}
        title={t('orders.filter.title')}
        fields={orderFields}
        sorts={orderSorts}
        value={list.state}
        customFields={customFieldsConfig}
        onApply={list.apply}
        onClose={() => setShowFilters(false)}
      />

      {/* Overlay to close dropdowns when clicking outside - positioned behind dropdowns */}
      {/* On web, we use document click listener instead of overlay */}
      {/* Exclude status dropdowns from overlay - they handle their own closing */}
      {Platform.OS !== 'web' && (openDropdown || showDateRangePicker) && !statusModalOrderId && !paymentStatusModalOrderId && (
        <Pressable 
          style={styles.dropdownOverlay} 
          onPress={handleCloseDropdowns}
          android_ripple={null}
        />
      )}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
    ...(Platform.OS === 'web' && {
      width: '100%',
      maxWidth: '100%',
    }),
  },
  scopedBannerWrapper: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
  },
  scopedExitRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.xs,
  },
  scopedExitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.xs,
  },
  scopedExitBannerText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  filtersContainer: {
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    padding: spacing.md,
    gap: spacing.md,
    ...designTokens.shadows.sm,
    position: 'relative',
    zIndex: 10000,
    overflow: 'visible', // Allow dropdowns to extend beyond container
    ...(Platform.OS === 'web' && {
      overflow: 'visible',
    }),
  },
  filterRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    position: 'relative',
  },
  filterDropdownWrapper: {
    flex: 1,
    minWidth: 120,
    position: 'relative',
    zIndex: 10000,
    elevation: 20, // For Android
  },
  filterDropdown: {
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.sm,
  },
  filterDropdownOpen: {
    borderColor: colors.primary,
    borderWidth: 2,
  },
  filterDropdownLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginBottom: spacing.xs / 2,
    fontFamily: typography.fontFamily.medium,
  },
  filterDropdownValue: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  filterDropdownText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    flex: 1,
  },
  dropdownOptions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: spacing.xs,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: colors.textDark,
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    zIndex: 10001,
    elevation: 25, // For Android - highest elevation
    overflow: 'hidden',
    ...(Platform.OS !== 'web' && {
      minWidth: 280, // Wide enough to prevent word breaks (longest text: "Quick Reply", "Confirmed", "Refunded")
      width: 'auto',
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      position: 'absolute', // Keep absolute for proper positioning
      minWidth: 280,
    }),
  },
  dropdownOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    width: '100%',
    minWidth: '100%',
  },
  dropdownOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    flex: 1,
    flexShrink: 0, // Prevent text from shrinking
    ...(Platform.OS !== 'web' && {
      flexWrap: 'nowrap', // Prevent text wrapping
    }),
  },
  dropdownOptionTextActive: {
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
    fontFamily: typography.fontFamily.medium,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalOptionText: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  modalOptionTextActive: {
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
    fontFamily: typography.fontFamily.medium,
  },
  dateRangeWrapper: {
    flex: 1,
    minWidth: 200,
    position: 'relative',
    zIndex: 10000,
    elevation: 20, // For Android
  },
  dateRangeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    minHeight: 40,
    gap: spacing.xs,
  },
  dateRangeText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  dateRangeClear: {
    padding: spacing.xs / 2,
  },
  dateRangeDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: spacing.xs,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: colors.textDark,
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    zIndex: 10001,
    elevation: 25, // For Android - highest elevation
    overflow: 'hidden',
    ...(Platform.OS !== 'web' && {
      minWidth: 280, // Wide enough to prevent word breaks
      width: 'auto',
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      minWidth: 280,
    }),
  },
  dateRangePickerContent: {
    padding: spacing.md,
    gap: spacing.md,
  },
  dateRangeInputRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  dateRangeInput: {
    flex: 1,
  },
  dateRangeLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.medium,
  },
  dateRangeTextInput: {
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    minHeight: 48,
  },
  dateRangeActions: {
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'flex-end',
  },
  dateRangeActionButton: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    minWidth: 100,
    alignItems: 'center',
  },
  dateRangeClearButton: {
    backgroundColor: colors.background.primary,
    borderWidth: 1,
    borderColor: colors.border,
  },
  dateRangeClearButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
  },
  dateRangeApplyButton: {
    backgroundColor: colors.primary,
  },
  dateRangeApplyButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.white,
    fontFamily: typography.fontFamily.medium,
  },
  dateIcon: {
    marginRight: spacing.xs,
  },
  searchContainer: {
    flex: 2,
    minWidth: 150,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
    minHeight: 40,
    zIndex: 1, // Lower z-index than dropdowns
  },
  searchIcon: {
    marginRight: spacing.xs,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    paddingVertical: spacing.xs,
    fontFamily: typography.fontFamily.regular,
  },
  clearFiltersButtonCompact: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: withAlpha(colors.error, 0.06),
    borderWidth: 1,
    borderColor: withAlpha(colors.error, 0.19),
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1, // Lower z-index than dropdowns
  },
  listContent: {
    // Was spacing.lg (24px, 36px on web) per side — too much on phones
    padding: spacing.md,
    gap: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg, // Add padding for bottom tabs on mobile
    ...(Platform.OS !== 'web' && {
      zIndex: 1, // Lower z-index than dropdowns and bottom tabs (only on mobile)
    }),
    ...(Platform.OS === 'web' && {
      position: 'relative',
    }),
  },
  orderCard: {
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    position: 'relative' as any,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      overflow: 'visible' as any,
    }),
  },
  orderCardInGrid: {
    flex: 1,
    minWidth: 0,
  },
  gridRow: {
    gap: spacing.md,
  },
  orderCardWithOpenDropdown: {
    zIndex: 10009, // Higher z-index when dropdown is open, but still below bottom tabs
    elevation: 29,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 2147483646,
      isolation: 'isolate' as any,
      overflow: 'visible' as any,
    }),
  },
  orderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  orderHeaderLeft: {
    flex: 1,
    minWidth: 0,
    marginRight: spacing.sm,
  },
  orderNumberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  orderNumber: {
    fontSize: typography.fontSize.base,
    flexShrink: 1,
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary,
  },
  copyButton: {
    padding: spacing.xs,
  },
  customerName: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    textAlign: 'left',
  },
  orderHeaderRight: {
    alignItems: 'flex-end',
    flexShrink: 0,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 2147483647,
      overflow: 'visible' as any,
    }),
  },
  statusDropdownWrapper: {
    zIndex: 10010,
    elevation: 30,
    ...(Platform.OS === 'web' && {
      zIndex: 2147483647,
      position: 'relative' as any,
      isolation: 'isolate' as any,
      overflow: 'visible' as any,
    }),
  },
  statusBadgeWrapper: {
    position: 'relative',
    zIndex: 10010,
    elevation: 30,
  },
  statusBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
  },
  statusText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
  },
  statusDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: spacing.xs,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: colors.textDark,
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    zIndex: 10003,
    elevation: 25,
    overflow: 'hidden',
    minWidth: 200,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    }),
  },
  statusDropdownOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  statusDropdownOptionActive: {
    backgroundColor: withAlpha(colors.primary, 0.06),
  },
  statusDropdownOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    flex: 1,
  },
  statusDropdownOptionTextActive: {
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
    fontFamily: typography.fontFamily.medium,
  },
  orderDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginBottom: spacing.sm,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 0,
      overflow: 'visible' as any,
    }),
  },
  orderDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
  },
  orderDetailText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  orderFooter: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 0,
    }),
  },
  orderTotal: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary,
  },
  orderActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 0,
    }),
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    padding: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primaryTransparent,
  },
  actionButtonText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  editInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.sm,
    marginTop: spacing.xs,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    backgroundColor: colors.background.surface,
  },
  editTextArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  selectButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  statusButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  statusButtonActive: {
    borderWidth: 2,
  },
  statusButtonText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  centerText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  retryButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    borderRadius: 8,
  },
  retryButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
  },
  loadingMoreContainer: {
    padding: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  loadingMoreText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  dropdownOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 9999, // Lower than dropdowns (10001+)
    elevation: 19, // Lower than dropdowns (25)
    ...(Platform.OS === 'web' && {
      position: 'fixed',
      pointerEvents: 'auto',
    }),
  },
  emptyTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    borderRadius: 8,
  },
  createButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
  },
  modalOverlay: {
    flex: 1,
    ...overlayBackdropStyle(),
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  modalContent: {
    backgroundColor: colors.background.surface,
    borderRadius: 16,
    width: '100%',
    maxWidth: 600,
    maxHeight: '80%',
    ...(Platform.OS === 'web' && {
      boxSizing: 'border-box',
    }),
    ...overlayCardLook(),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary,
  },
  modalCloseButton: {
    padding: spacing.xs,
  },
  modalBody: {
    padding: spacing.md,
    maxHeight: 400,
  },
  detailSection: {
    marginBottom: spacing.lg,
  },
  detailLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  detailValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  detailValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.text.primary,
    flex: 1,
  },
  detailSubValue: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  itemName: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
  },
  itemQuantity: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginHorizontal: spacing.md,
  },
  itemPrice: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.text.primary,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.md,
    borderTopWidth: 2,
    borderTopColor: colors.border,
  },
  totalLabel: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary,
  },
  totalValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.md,
    padding: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  modalActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 8,
  },
  modalActionButtonPrimary: {
    backgroundColor: colors.primary,
  },
  modalActionButtonSecondary: {
    backgroundColor: colors.background.primary,
    borderWidth: 1,
    borderColor: colors.border,
  },
  modalActionButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  modalActionButtonTextPrimary: {
    color: colors.white,
  },
  modalActionButtonTextSecondary: {
    color: colors.text.primary,
  },
  modalLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.text.primary,
    marginBottom: spacing.md,
  },
  statusModalContent: {
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    maxHeight: '80%',
    width: '90%',
    maxWidth: 400,
    ...(Platform.OS === 'web' && {
      position: 'absolute' as any,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)' as any,
    }),
  },
  statusOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  statusOptionActive: {
    backgroundColor: withAlpha(colors.primary, 0.06),
  },
  statusOptionText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    flex: 1,
    marginLeft: spacing.sm,
  },
  statusOptionTextActive: {
    color: colors.primary as any,
    fontWeight: typography.fontWeight.bold as any,
    fontFamily: typography.fontFamily.medium,
  },
});

